import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';

type OrgContextValue = {
  orgId: string | null;
  orgSlug: string | null;
  displayName: string | null;
  loading: boolean;
  source: 'jwt' | 'subdomain' | 'path' | 'kiosk' | 'env' | null;
};

const OrgContext = createContext<OrgContextValue | null>(null);

const DEFAULT_ORG_SLUG = process.env.REACT_APP_DEFAULT_ORG_SLUG || '';

const APP_BASE_DOMAIN = (process.env.REACT_APP_BASE_DOMAIN || 'packflow.insolitum.ai').toLowerCase();

function slugFromHost(): string | null {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname.toLowerCase();
  // <slug>.packflow.insolitum.ai -> "slug"; apex/app/www/api, localhost, IP, vercel -> null
  if (!host || host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return null;
  if (host === APP_BASE_DOMAIN) return null;
  const suffix = '.' + APP_BASE_DOMAIN;
  if (!host.endsWith(suffix)) return null;
  const first = host.slice(0, -suffix.length).split('.')[0];
  if (!first || ['www', 'app', 'api'].includes(first)) return null;
  return /^[a-z0-9-]{3,40}$/.test(first) ? first : null;
}

// Kiosk PackFlow Station / apex z ?org=: slug zapamiętany w localStorage.
function slugFromStore(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const q = new URLSearchParams(window.location.search).get('org');
    if (q && /^[a-z0-9-]{3,40}$/.test(q)) localStorage.setItem('packflow.org_slug', q);
  } catch { /* brak search */ }
  const s = localStorage.getItem('packflow.org_slug');
  return s && /^[a-z0-9-]{3,40}$/.test(s) ? s : null;
}

function slugFromPath(): string | null {
  if (typeof window === 'undefined') return null;
  const seg = window.location.pathname.split('/').filter(Boolean)[0];
  if (!seg) return null;
  // reserved top-level paths - NIE traktujemy ich jako slug org
  const reserved = new Set([
    'login', 'admin', 'packing', 'orders', 'statistics', 'settings',
    'templates', 'api', 'webhooks', 'dashboard',
    'labels', 'reports', 'ucs', 'super-admin', 'packing-station'
  ]);
  if (reserved.has(seg)) return null;
  return /^[a-z0-9-]{3,40}$/.test(seg) ? seg : null;
}

export function OrgProvider({ children }: { children: React.ReactNode }) {
  const { claims, loading: authLoading } = useAuth();
  const [displayName, setDisplayName] = useState<string | null>(null);

  const resolved = useMemo<{ slug: string | null; id: string | null; source: OrgContextValue['source'] }>(() => {
    if (claims.org_id) return { slug: null, id: claims.org_id, source: 'jwt' };
    const sub = slugFromHost();
    if (sub) return { slug: sub, id: null, source: 'subdomain' };
    const store = slugFromStore();
    if (store) return { slug: store, id: null, source: 'kiosk' };
    const p = slugFromPath();
    if (p) return { slug: p, id: null, source: 'path' };
    if (DEFAULT_ORG_SLUG) return { slug: DEFAULT_ORG_SLUG, id: null, source: 'env' };
    return { slug: null, id: null, source: null };
  }, [claims.org_id]);

  useEffect(() => {
    // Placeholder: docelowo GET /api/org/current zwroci display_name.
    // Dzis nie blokujemy UI - zostawiamy null.
    setDisplayName(null);
  }, [resolved.id, resolved.slug]);

  const value: OrgContextValue = {
    orgId: resolved.id,
    orgSlug: resolved.slug,
    displayName,
    loading: authLoading,
    source: resolved.source
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be inside OrgProvider');
  return ctx;
}
